import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { attendanceAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Clock, TrendingUp, AlertTriangle, CheckCircle, Upload, Sparkles, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  present: 'bg-green-100 text-green-800',
  absent: 'bg-red-100 text-red-800',
  half_day: 'bg-yellow-100 text-yellow-800',
  weekend: 'bg-gray-100 text-gray-600',
  holiday: 'bg-blue-100 text-blue-800',
  work_from_home: 'bg-purple-100 text-purple-800',
  on_leave: 'bg-orange-100 text-orange-800',
};

const anomalyLabels = {
  late_entry: 'Late Entry',
  early_exit: 'Early Exit',
  missed_checkout: 'Missed Checkout',
  overtime: 'Overtime',
  short_hours: 'Short Hours',
};

export default function MyAttendancePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState([]);
  const [insights, setInsights] = useState(null);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      from: firstDay.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, recordsRes] = await Promise.all([
        attendanceAPI.getMyStats(),
        attendanceAPI.getDays({ from: dateRange.from, to: dateRange.to })
      ]);
      setStats(statsRes.data);
      setRecords(recordsRes.data.records || []);
      
      // Check for cached insights
      if (statsRes.data.has_ai_insights) {
        try {
          const insightsRes = await attendanceAPI.generateInsights(
            statsRes.data.employee_id,
            dateRange.from,
            dateRange.to,
            false
          );
          setInsights(insightsRes.data);
        } catch (e) {
          // Insights might not be available yet
        }
      }
    } catch (error) {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerateInsights = async () => {
    if (!stats?.employee_id) return;
    setGeneratingInsights(true);
    try {
      const res = await attendanceAPI.generateInsights(
        stats.employee_id,
        dateRange.from,
        dateRange.to,
        true
      );
      setInsights(res.data);
      toast.success('AI insights generated!');
    } catch (error) {
      toast.error('Failed to generate insights');
    } finally {
      setGeneratingInsights(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const summary = stats?.summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            My Attendance
          </h1>
          <p className="text-muted-foreground">{stats?.period?.month || 'Current Month'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
            className="w-36"
          />
          <span className="text-muted-foreground">to</span>
          <Input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
            className="w-36"
          />
          <Button variant="outline" size="icon" onClick={fetchData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm text-muted-foreground">Present</span>
            </div>
            <p className="text-2xl font-bold">{summary.days_present || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-muted-foreground">Absent</span>
            </div>
            <p className="text-2xl font-bold">{summary.days_absent || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              <span className="text-sm text-muted-foreground">Late Entries</span>
            </div>
            <p className="text-2xl font-bold">{summary.late_entries || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-muted-foreground">Avg Hours</span>
            </div>
            <p className="text-2xl font-bold">{summary.average_daily_hours?.toFixed(1) || '0.0'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-purple-600" />
              <span className="text-sm text-muted-foreground">On-Time Rate</span>
            </div>
            <p className="text-2xl font-bold">{summary.on_time_arrival_rate?.toFixed(0) || 0}%</p>
          </CardContent>
        </Card>
        <Card className={stats?.productivity_score ? 'border-primary' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm text-muted-foreground">Score</span>
            </div>
            <p className={`text-2xl font-bold ${getScoreColor(stats?.productivity_score || 0)}`}>
              {stats?.productivity_score || insights?.productivity_score || '—'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="calendar" className="space-y-4">
        <TabsList>
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
          <TabsTrigger value="records">Detailed Records</TabsTrigger>
        </TabsList>

        {/* Calendar View */}
        <TabsContent value="calendar">
          <Card>
            <CardHeader>
              <CardTitle>Attendance Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2 text-center text-sm">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="font-medium text-muted-foreground p-2">{day}</div>
                ))}
                {records.length > 0 ? (
                  (() => {
                    // Group records by date
                    const recordMap = {};
                    records.forEach(r => { recordMap[r.date] = r; });
                    
                    // Generate calendar days
                    const start = new Date(dateRange.from);
                    const end = new Date(dateRange.to);
                    const days = [];
                    
                    // Add empty cells for days before start
                    const startDay = start.getDay();
                    for (let i = 0; i < startDay; i++) {
                      days.push(<div key={`empty-${i}`} className="p-2" />);
                    }
                    
                    // Add actual days
                    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                      const dateStr = d.toISOString().split('T')[0];
                      const rec = recordMap[dateStr];
                      const status = rec?.status || 'unknown';
                      const anomalies = rec?.anomalies || [];
                      
                      days.push(
                        <div
                          key={dateStr}
                          className={`p-2 rounded-lg border ${statusColors[status] || 'bg-gray-50'} relative group`}
                          title={`${dateStr}: ${status}${anomalies.length ? ` (${anomalies.join(', ')})` : ''}`}
                        >
                          <div className="font-medium">{d.getDate()}</div>
                          {rec?.total_hours > 0 && (
                            <div className="text-xs">{rec.total_hours.toFixed(1)}h</div>
                          )}
                          {anomalies.length > 0 && (
                            <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full" />
                          )}
                        </div>
                      );
                    }
                    
                    return days;
                  })()
                ) : (
                  <div className="col-span-7 text-center py-8 text-muted-foreground">
                    No attendance records found for this period
                  </div>
                )}
              </div>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t">
                {Object.entries(statusColors).map(([status, color]) => (
                  <div key={status} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded ${color}`} />
                    <span className="text-sm capitalize">{status.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights */}
        <TabsContent value="insights">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    AI Attendance Insights
                  </CardTitle>
                  <CardDescription>
                    Powered by Gemini - personalized analysis of your attendance patterns
                  </CardDescription>
                </div>
                <Button onClick={handleGenerateInsights} disabled={generatingInsights}>
                  {generatingInsights ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      {insights ? 'Refresh Insights' : 'Generate Insights'}
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {insights ? (
                <div className="space-y-6">
                  {/* Productivity Score */}
                  <div className="flex items-center justify-center">
                    <div className="text-center">
                      <div className={`text-6xl font-bold ${getScoreColor(insights.productivity_score)}`}>
                        {insights.productivity_score}
                      </div>
                      <p className="text-muted-foreground mt-1">Productivity Score</p>
                    </div>
                  </div>

                  {/* Working Pattern Insights */}
                  <div>
                    <h3 className="font-semibold mb-3">Working Pattern Insights</h3>
                    <ul className="space-y-2">
                      {insights.working_pattern_insights?.map((insight, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-1 shrink-0" />
                          <span>{insight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Areas of Improvement */}
                  <div>
                    <h3 className="font-semibold mb-3">Areas of Improvement</h3>
                    <ul className="space-y-2">
                      {insights.areas_of_improvement?.map((area, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <TrendingUp className="h-4 w-4 text-blue-600 mt-1 shrink-0" />
                          <span>{area}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Generated: {new Date(insights.generated_at).toLocaleString()}
                  </p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">
                    Get AI-powered insights about your attendance patterns
                  </p>
                  <Button onClick={handleGenerateInsights} disabled={generatingInsights}>
                    Generate Insights
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Detailed Records */}
        <TabsContent value="records">
          <Card>
            <CardHeader>
              <CardTitle>Attendance Records</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-3 font-medium">Date</th>
                      <th className="p-3 font-medium">Status</th>
                      <th className="p-3 font-medium">Check In</th>
                      <th className="p-3 font-medium">Check Out</th>
                      <th className="p-3 font-medium">Hours</th>
                      <th className="p-3 font-medium">Anomalies</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {records.map(rec => (
                      <tr key={rec.id}>
                        <td className="p-3">
                          {new Date(rec.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </td>
                        <td className="p-3">
                          <Badge className={statusColors[rec.status] || ''}>
                            {rec.status?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="p-3 font-mono">{rec.first_check_in || '—'}</td>
                        <td className="p-3 font-mono">{rec.last_check_out || '—'}</td>
                        <td className="p-3">{rec.total_hours?.toFixed(2) || '0.00'}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {rec.anomalies?.map(a => (
                              <Badge key={a} variant="outline" className="text-xs">
                                {anomalyLabels[a] || a}
                              </Badge>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {records.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-muted-foreground">
                          No attendance records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
